import { useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { artists, getArtist, DISCIPLINE_LABEL, SLUG_REDIRECTS } from "@/data/artists";
import { useCatalogVersion } from "@/data/catalogLive";
import ArtworkDetail from "@/pages/ArtworkDetail";
import { useSEO } from "@/hooks/useSEO";

/**
 * Every /artistas/:slug route renders the redesigned ArtworkDetail (aka
 * "Audioguía LATIR" ficha). Kept as a thin resolver so the routing and
 * prev/next logic stay isolated from the presentational component.
 */
export default function ArtistDetail() {
  useCatalogVersion();
  const { slug } = useParams();
  const { lang } = useLang();
  const artist = getArtist(slug);

  useSEO({
    title: artist
      ? `${artist.name} | LATIR: El placer de vivir — Arteconciencia`
      : "Artistas | LATIR — Arteconciencia",
    description: artist
      ? `Conoce la obra de ${artist.name} en LATIR: El placer de vivir, la exposición de arte contemporáneo de Arteconciencia en el Palacio Nacional de El Salvador.`
      : undefined,
    canonical: artist ? `/artistas/${artist.slug}` : "/artistas",
  });

  const { prev, next } = useMemo(() => {
    const i = artists.findIndex((a) => a.slug === slug);
    return {
      prev: artists[(i - 1 + artists.length) % artists.length],
      next: artists[(i + 1) % artists.length],
    };
  }, [slug]);

  if (!artist) {
    const redirect = SLUG_REDIRECTS[slug];
    if (redirect === "__agenda__") return <Navigate to="/agenda" replace />;
    return <Navigate to={redirect ? `/artistas/${redirect}` : "/artistas"} replace />;
  }

  return (
    <ArtworkDetail
      artist={artist}
      prev={prev}
      next={next}
      discipline={DISCIPLINE_LABEL[artist.discipline]?.[lang] || ""}
      num={String(artist.n || 1).padStart(2, "0")}
      totalCount={artists.length}
    />
  );
}
